import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Search, X, Loader2, ArrowLeft, BadgeCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { searchUsers } from "../../services/searchService";
import FetchErrorState from "../Common/FetchErrorState";

const PAGE_SIZE = 10;

const getProfilePicture = (profilePic) => {
  if (profilePic && typeof profilePic === "object") {
    return profilePic.thumbnail || profilePic.small || profilePic.medium;
  }
  if (typeof profilePic === "string") {
    return profilePic;
  }
  return null;
};

const getInitials = (name = "User") => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

const SearchBar = (props) => {
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [initialSearchDone, setInitialSearchDone] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const { apiCall } = useAuth();

  const fetchOptions = async (searchText, pageNumber) => {
    if (!searchText.trim()) {
      setOptions([]);
      setInitialSearchDone(false);
      setSearchError(null);
      return;
    }

    setLoading(true);
    if (pageNumber === 1) setSearchError(null);

    try {
      const res = await searchUsers(
        apiCall,
        `${props.searchApi}?q=${encodeURIComponent(searchText)}&page=${pageNumber}&limit=${PAGE_SIZE}`,
      );

      const payload = res?.data || {};
      const items = Array.isArray(payload.items) ? payload.items : [];

      setOptions((prev) => (pageNumber === 1 ? items : [...prev, ...items]));

      setHasMore(items.length === PAGE_SIZE);
      setInitialSearchDone(true);
    } catch (err) {
      if (pageNumber === 1) {
        setOptions([]);
        setInitialSearchDone(true);
        setSearchError("We couldn't complete this search.");
      }
      // Pagination (page > 1) failing stays silent — the results already
      // shown remain intact; scrolling again naturally retries it.
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setPage(1);
        setInitialSearchDone(false);
        fetchOptions(val, 1);
      }, 350),
    [apiCall, props.searchApi],
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSelectedIndex(-1);

    if (value.trim()) {
      setIsOpen(true);
      debouncedSearch(value);
    } else {
      setIsOpen(false);
      debouncedSearch.cancel();
      setOptions([]);
      setHasMore(true);
      setInitialSearchDone(false);
      setSearchError(null);
    }
  };

  const handleClear = () => {
    setInputValue("");
    setOptions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    setInitialSearchDone(false);
    setSearchError(null);
    inputRef.current?.focus();
  };

  const handleBack = () => {
    setIsFocused(false);
    setIsOpen(false);
    setInputValue("");
    setOptions([]);
    setHasMore(true);
    setSelectedIndex(-1);
    setInitialSearchDone(false);
    setSearchError(null);
  };

  const handleSelect = (user) => {
    navigate(`/${user.username}`);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && options[selectedIndex]) {
          handleSelect(options[selectedIndex]);
        }
        break;
      case "Escape":
        handleBack();
        break;
      default:
        break;
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    if (
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50 &&
      hasMore &&
      !loading &&
      inputValue.trim()
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchOptions(inputValue, nextPage);
    }
  };

  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [selectedIndex]);

  // Prevent scrolling on body when search modal is open
  useEffect(() => {
    if (isFocused) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isFocused]);

  // Focused state - modal
  if (isFocused) {
    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/30 z-40" onClick={handleBack} />

        {/* Modal */}
        <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center p-0 md:p-4">
          <div className="w-full md:w-96 bg-white rounded-none md:rounded-2xl flex flex-col h-dvh md:h-[600px]">
            {/* Header */}
            <div className="border-b border-gray-200 p-4 flex-shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBack}
                  className="flex-shrink-0 text-gray-700 hover:text-gray-900 transition-colors md:hidden"
                >
                  <ArrowLeft size={24} />
                </button>

                <div className="flex-1 relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Search size={18} />
                  </div>

                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Search users..."
                    autoFocus
                    className="w-full pl-10 pr-10 py-2 bg-gray-100 rounded-full border-none outline-none text-sm"
                  />
                </div>

                <button
                  onClick={handleBack}
                  className="hidden md:flex flex-shrink-0 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Results Container - scrollable */}
            <div className="flex-1 overflow-y-auto bg-white">
              {loading && options.length === 0 && inputValue ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 size={24} className="animate-spin mr-2" />
                  <span className="text-sm">Searching...</span>
                </div>
              ) : !inputValue ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <p className="text-sm">Connect with writers from around the world</p>
                </div>
              ) : searchError && options.length === 0 ? (
                <FetchErrorState
                  message={searchError}
                  onRetry={() => fetchOptions(inputValue, 1)}
                />
              ) : initialSearchDone && options.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <Search size={28} className="text-gray-400" />
                  </div>
                  <p className="text-sm font-medium">No results found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try searching for something else
                  </p>
                </div>
              ) : (
                <div ref={listRef} onScroll={handleScroll} className="py-2">
                  {options.map((user, index) => (
                    <div
                      key={user._id}
                      onClick={() => handleSelect(user)}
                      className={`flex items-center gap-3 px-4 py-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                        index === selectedIndex ? "bg-gray-100" : ""
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {getProfilePicture(user.profilePicture) ? (
                          <img
                            src={getProfilePicture(user.profilePicture)}
                            alt={user.username}
                            className="w-11 h-11 rounded-full object-cover"
                          />
                        ) : user?.name ? (
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            <span className="text-slate-600 font-bold text-xs">
                              {getInitials(user?.name)}
                            </span>
                          </div>
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            <svg
                              className="w-6 h-6 text-slate-400"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <circle cx="12" cy="8" r="4" />
                              <path d="M12 14c-4 0-6 2-6 4v2h12v-2c0-2-2-4-6-4z" />
                            </svg>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {user.username}
                          </p>
                          {user.isVerified && (
                            <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {loading && options.length > 0 && (
                    <div className="flex items-center justify-center py-4 border-t border-gray-100">
                      <Loader2
                        size={20}
                        className="text-gray-400 animate-spin"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Normal/collapsed layout
  return (
    <div className="w-full">
      <div className="max-w-xl mx-auto">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Search size={18} />
          </div>

          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setIsFocused(true);
              if (inputValue.trim()) setIsOpen(true);
            }}
            placeholder="Search users..."
            className="w-full pl-10 pr-10 py-2 bg-gray-100 rounded-xl border-none outline-none focus:bg-white focus:ring-2 focus:ring-gray-200 transition-all text-sm"
          />

          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {loading ? (
              <Loader2 size={16} className="text-gray-400 animate-spin" />
            ) : inputValue ? (
              <button
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={16} />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
